import React, { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import moment from 'moment';
import IMAGES from '../../../../constant/images';

/**
 * BookingCard Component
 */
const BookingCard = ({ booking, onCancel, onReschedule, onJoinNow, onRebook, onViewDocuments, currentTime }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [canJoinNow, setCanJoinNow] = useState(false);

  // Appointment status constants
  const APPOINTMENT_STATUS = {
    SCHEDULED: "SCHEDULED",
    IN_PROGRESS: "IN_PROGRESS",
    PENDING: "PENDING",
    COMPLETED: "COMPLETED",
    IN_COMPLETE: "IN_COMPLETE",
    EXPIRED: "EXPIRED",
    CANCELED: "CANCELED",
    COUNSELOR_UNAVAILABLE: "COUNSELOR_UNAVAILABLE",
  };

  // Calculate join window and time left
  useEffect(() => {
    if (
      booking.status === APPOINTMENT_STATUS.SCHEDULED &&
      booking.date &&
      booking.startTime &&
      booking.endTime
    ) {
      const now = currentTime || moment();
      const start = moment(`${booking.date} ${booking.startTime}`, 'YYYY-MM-DD HH:mm:ss');
      const end = moment(`${booking.date} ${booking.endTime}`, 'YYYY-MM-DD HH:mm:ss');
      const minStartTime = start.clone().subtract(5, 'minutes');

      // Check if can join (5 minutes before to end time)
      const canJoin = now.isBetween(minStartTime, end);
      setCanJoinNow(canJoin);

      if (!canJoin && now.isBefore(start)) {
        // Calculate time until appointment
        const duration = moment.duration(start.diff(now));
        const minutes = Math.floor(duration.asMinutes());

        if (minutes < 60) {
          setTimeLeft(`${minutes}min left`);
        } else {
          const hours = Math.floor(duration.asHours());
          const mins = minutes % 60;
          setTimeLeft(mins > 0 ? `${hours}h ${mins}min left` : `${hours}h left`);
        }
      }
    }
  }, [booking, currentTime]);

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    return moment(date).format('Do MMM YYYY');
  };

  // Format time range
  const formatTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return '';
    const start = moment(startTime, 'HH:mm:ss').format('h:mm a');
    const end = moment(endTime, 'HH:mm:ss').format('h:mm a');
    return `${start} - ${end}`;
  };

  // Format duration
  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '';
    const start = moment(startTime, 'HH:mm:ss');
    const end = moment(endTime, 'HH:mm:ss');
    const duration = moment.duration(end.diff(start));
    const minutes = duration.asMinutes();
    return `${minutes} min`;
  };

  // Get mode icon and label
  const getModeDisplay = (mode) => {
    switch (mode) {
      case 'VIDEO':
        return {
          icon: IMAGES.video_icon,
          label: 'Video Consultation'
        };
      case 'AUDIO':
        return {
          icon: IMAGES.audio_call,
          label: 'Audio Consultation'
        };
      case 'CHAT':
        return {
          icon: IMAGES.chat_icon,
          label: 'Chat Consultation'
        };
      case 'INPERSON':
        return {
          icon: IMAGES.in_person,
          label: 'In-Person'
        };
      default:
        return {
          icon: IMAGES.video_icon,
          label: 'Video Consultation'
        };
    }
  };

  // Get effective status 
  const getEffectiveStatus = () => {
    if (booking.status === APPOINTMENT_STATUS.SCHEDULED && booking.currentStatus) {
      return booking.currentStatus;
    }
    return booking.status;
  };

  // Get status badge 
  const getStatusBadge = () => {
    const effectiveStatus = getEffectiveStatus();

    // Handle Missed statuses 
    if (effectiveStatus === 'STUDENT MISSED' || effectiveStatus === 'STUDENT_MISSED') {
      return {
        label: 'Missed by You',
        dotColor: 'bg-[#F14651]'
      };
    }

    if (effectiveStatus === 'COUNSELLOR MISSED' || effectiveStatus === 'COUNSELLOR_MISSED' || effectiveStatus === 'MISSED BY COUNSELLOR') {
      return {
        label: 'Missed by Counsellor',
        dotColor: 'bg-[#F14651]'
      };
    }

    if (effectiveStatus === 'MISSED BY BOTH' || effectiveStatus === 'MISSED_BY_BOTH') {
      return {
        label: 'Missed by Both',
        dotColor: 'bg-[#F14651]'
      };
    }

    // Handle regular status values - Only showing specific statuses
    switch (effectiveStatus) {
      case APPOINTMENT_STATUS.SCHEDULED:
      case 'UPCOMING':
      case 'Upcoming':
        return {
          label: 'Upcoming',
          dotColor: 'bg-[#FFE375]'
        };
      case APPOINTMENT_STATUS.COMPLETED:
        return {
          label: 'Completed',
          dotColor: 'bg-[#98FF8A]'
        };
      case APPOINTMENT_STATUS.COUNSELOR_UNAVAILABLE:
      case 'COUNSELLOR UNAVAILABLE':
        return {
          label: 'Practitioner Unavailable',
          dotColor: 'bg-[#FF7439]'
        };
      case APPOINTMENT_STATUS.CANCELED:
      case 'CANCELLED':
        return {
          label: 'Cancelled',
          dotColor: 'bg-[#FF7439]'
        };
      // All other statuses fall under Incomplete
      case APPOINTMENT_STATUS.IN_COMPLETE:
      case 'INCOMPLETE':
      case APPOINTMENT_STATUS.IN_PROGRESS:
      case APPOINTMENT_STATUS.PENDING:
      case APPOINTMENT_STATUS.EXPIRED:
      case 'RISK MISSING':
      default:
        return {
          label: 'Incomplete',
          dotColor: 'bg-[#FFAF77]'
        };
    }
  };

  const modeDisplay = getModeDisplay(booking.mode);
  const statusBadge = getStatusBadge();
  const isPastOrCancelled =
    booking.status === APPOINTMENT_STATUS.CANCELED ||
    booking.status === APPOINTMENT_STATUS.COUNSELOR_UNAVAILABLE ||
    booking.status === APPOINTMENT_STATUS.EXPIRED;

  // Check if appointment is missed
  const isMissedAppointment = () => {
    const effectiveStatus = getEffectiveStatus();
    return (
      effectiveStatus === 'STUDENT MISSED' ||
      effectiveStatus === 'STUDENT_MISSED' ||
      effectiveStatus === 'COUNSELLOR MISSED' ||
      effectiveStatus === 'COUNSELLOR_MISSED' ||
      effectiveStatus === 'MISSED BY COUNSELLOR' ||
      effectiveStatus === 'MISSED BY BOTH' ||
      effectiveStatus === 'MISSED_BY_BOTH'
    );
  };

  // Check if appointment is truly upcoming (future and not missed)
  const isUpcomingAppointment = () => {
    const now = currentTime || moment();
    const endMoment = moment(`${booking.date} ${booking.endTime}`, 'YYYY-MM-DD HH:mm:ss');
    const isFuture = endMoment.isAfter(now);

    return (
      [APPOINTMENT_STATUS.SCHEDULED, APPOINTMENT_STATUS.IN_PROGRESS, APPOINTMENT_STATUS.PENDING].includes(booking.status) &&
      isFuture &&
      !isMissedAppointment()
    );
  };

  return (
    <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm hover:shadow-md transition-shadow px-[16px] pt-[12px] pb-[20px] font-poppins">
      {/* Header: Booking ID, Follow Up Badge, Mode, Status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <p className="text-[13px] leading-[24px] text-[#64748B] font-medium mb-0">Booking ID</p>
          <p className="text-[13px] leading-[24px] text-[#171717] font-semibold mb-0">
            {booking.appointmentId || 'N/A'}
          </p>
          {booking.isFollowUp && (
            <div className="bg-[#F0FDFA] px-2.5 py-0.5 rounded-full">
              <p className="text-[11px] leading-normal text-[#0D9488] font-semibold mb-0">Follow Up</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-6">
          {/* Mode Badge */}
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#EFF6FF]">
            <img src={modeDisplay.icon} alt={modeDisplay.label} className="w-[14px] h-[14px] object-contain flex-shrink-0" />
            <span className="text-[11px] leading-normal font-semibold text-[#2563EB]">{modeDisplay.label}</span>
          </div>

          {/* Status Badge  */}
          <div className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${statusBadge.dotColor} flex-shrink-0`}></div>
            <span className="text-[#4B61FF] text-[13px] font-semibold">{statusBadge.label}</span>
          </div>
        </div>
      </div>

      {/* Separator */}
      <div className="h-px bg-[#F1F5F9] mb-4"></div>

      {/* Main Content */}
      <div className="flex items-center justify-between gap-4">
        {/* Left: Practitioner Info */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            {/* Practitioner Image */}
            <div className="flex flex-col items-center gap-2">
              <div className="w-[96px] h-[96px] rounded-xl border-[1px] border-solid border-[#E2E8F0] overflow-hidden">
                {booking.counsellorImageURL ? (
                  <img
                    src={booking.counsellorImageURL}
                    alt={`${booking.counsellorFirstName} ${booking.counsellorLastName}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[#F8FAFC] flex items-center justify-center">
                    <img src={IMAGES.user_filled} alt="user" className="w-14 h-14 object-contain" />
                  </div>
                )}
              </div>
            </div>

            {/* Practitioner Details */}
            <div className="flex flex-col gap-2">
              {/* Name and Rating */}
              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-3">
                  <h3 className="text-base leading-normal font-semibold text-[#171717] mb-0">
                    {booking.counsellorTitle || 'Dr.'} {booking.counsellorFirstName} {booking.counsellorLastName}
                  </h3>
                  {booking.counsellorRating && (
                    <div className="flex items-start gap-1">
                      <Star size={16} className="fill-[#F59E0B] text-[#F59E0B] flex-shrink-0" />
                      <p className="text-sm leading-normal text-[#334155] mb-0">
                        {booking.counsellorRating}
                      </p>
                    </div>
                  )}
                </div>

                {/* Specialization */}
                {booking.counsellorSpecialization && (
                  <div className="flex items-center gap-1">
                    <span className="text-[13px] leading-normal text-[#64748B]">
                      {booking.counsellorSpecialization}
                    </span>
                  </div>
                )}
              </div>

              {/* Rescheduled Badge */}
              {booking.isRescheduled && (
                <div className="bg-[#FFF7ED] px-2.5 py-0.5 rounded-full inline-block self-start">
                  <span className="text-[11px] leading-normal text-[#EA580C] font-semibold">Rescheduled</span>
                </div>
              )}

              {/* Date, Time, Duration */}
              <div className="flex items-center gap-4">
                {/* Date */}
                <div className="flex items-center gap-1.5">
                  <img
                    src={IMAGES.calender_icon}
                    alt="calendar"
                    className="w-5 h-5 object-contain flex-shrink-0"
                  />
                  <span className="text-[13px] leading-normal font-medium text-[#334155]">
                    {formatDate(booking.date)}
                  </span>
                </div>

                {/* Time */}
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex-shrink-0">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M12 6v6l4 2" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"/>
                      <circle cx="12" cy="12" r="9" stroke="#2563EB" strokeWidth="2"/>
                    </svg>
                  </div>
                  <span className="text-[13px] leading-normal font-medium text-[#334155]">
                    {formatTimeRange(booking.startTime, booking.endTime)}
                  </span>
                </div>

                {/* Duration */}
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 flex-shrink-0">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M12 6v6l4 2" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round"/>
                      <circle cx="12" cy="12" r="8" stroke="#2563EB" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <span className="text-[13px] leading-normal font-medium text-[#334155]">
                    {formatDuration(booking.startTime, booking.endTime)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center gap-6">
          {/* Show different actions based on status */}
          {canJoinNow && booking.status === APPOINTMENT_STATUS.SCHEDULED && (
            <div className="flex flex-col items-end gap-2">
              <div className="bg-white px-1 py-1 rounded-lg">
                <span className="text-[13px] leading-[24px] text-[#4B61FF] font-semibold">{timeLeft}</span>
              </div>
              <button
                onClick={() => onJoinNow(booking)}
                className="btn-gradient border-0 pl-[14px] pr-2 rounded-xl flex items-center gap-1 h-9"
              >
                <span className="text-[13px] leading-[24px] text-white font-semibold">Join Now</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="flex-shrink-0">
                  <path d="M7.5 5l5 5-5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}

          {!canJoinNow && isUpcomingAppointment() && (
            <div className="flex items-center gap-6">
              <button
                onClick={() => onCancel(booking)}
                className="bg-white hover:bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] shadow-sm transition px-[14px] rounded-xl h-9 flex items-center justify-center"
              >
                <span className="text-[13px] leading-[24px] text-[#171717] font-semibold">Cancel</span>
              </button>
              <button
                onClick={() => onReschedule(booking)}
                className="btn-gradient border-0 pl-[14px] pr-2 rounded-xl flex items-center gap-1 h-9"
              >
                <span className="text-[13px] leading-[24px] text-white font-semibold">Re-schedule</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="flex-shrink-0">
                  <path d="M7.5 5l5 5-5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          )}

          {booking.status === APPOINTMENT_STATUS.COMPLETED && (
            <button
              onClick={() => onViewDocuments(booking)}
              className="btn-gradient border-0 pl-[14px] pr-2 rounded-xl flex items-center gap-1 h-9"
            >
              <span className="text-[13px] leading-[24px] text-white font-semibold">View Documents</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="flex-shrink-0">
                <path d="M7.5 5l5 5-5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          {/* Re-book button for Cancelled and Missed appointments */}
          {(booking.status === APPOINTMENT_STATUS.CANCELED ||
            booking.status === APPOINTMENT_STATUS.COUNSELOR_UNAVAILABLE ||
            isMissedAppointment()) && (
            <div className="flex flex-col items-end gap-2">
              {booking.status === APPOINTMENT_STATUS.CANCELED && (
                <div className="bg-[#FEF2F2] px-2.5 py-0.5 rounded-full">
                  <span className="text-[11px] leading-normal text-[#DC2626] font-semibold">You cancelled this appointment.</span>
                </div>
              )}
              <button
                onClick={() => onRebook(booking)}
                className="bg-white hover:bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] shadow-sm transition px-[14px] rounded-xl h-9 flex items-center justify-center"
              >
                <span className="text-[13px] leading-[24px] text-[#171717] font-semibold">Re-book</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingCard;
